import {ClientDevice, AssignmentInfo, AnyParticipant, SessionResult, SessionStatuses, SessionActivity, SessionSchedule} from 'interfaces';
import {SessionNote} from 'types';

export enum SessionFilePipelineStepStatus {
    Success = 'success',
    Error = 'error',
    InProgress = 'in_progress',
    Initial = 'initial'
}

export enum SessionFilePipelineStepName {
    Receive = 'receive',
    Upload = 'upload',
    Remove = 'remove'
}

export interface SessionFilePipelineStep {
    readonly step: SessionFilePipelineStepName;
    readonly status: SessionFilePipelineStepStatus;
    readonly message: string;
    readonly date: Date;
}

export interface SessionFilePipeline {
    readonly receive: SessionFilePipelineStep;
    readonly upload: SessionFilePipelineStep;
    readonly remove: SessionFilePipelineStep;
}

export enum SessionFilePlace {
    Internal = 'internal',
    External = 'external'
}

export type SessionFile = {
    id?: string,
    name?: string,
    place: SessionFilePlace,
    link?: string;
    internalLink?: string;
    pipeline: SessionFilePipeline;
    pipelineOverallStatus: SessionFilePipelineStepStatus;
    size?: number;
    accessibleStorage: boolean;
    previewable?: boolean;
}

export interface SessionHistory {
    readonly sessionId: string;
    readonly assignment: AssignmentInfo;
    readonly statuses: SessionStatuses;
    readonly result: SessionResult;
    readonly startDate: string;
    readonly endDate: string;
    readonly duration: string;
    readonly schedule: SessionSchedule;
    readonly participants: AnyParticipant[];
    readonly activities: SessionActivity[];
    readonly files?: SessionFile[];
    readonly notes?: SessionNote[];
    readonly hasAudit?: boolean;
}

export interface SessionHistoryFullStatistic {
    total: SessionHistoryStatistic;
    days: SessionHistoryStatisticDaily;
    months: SessionHistoryStatisticDaily;
}

export type SessionResultInformationDetails = {
    finishedBySystem: number,
    noStatus: number,
    statuses: {
        [status: string]: number
    }
}

export type ActivityStatistic = Partial<{
    uniqueClients: number;
    answered: number;
    missed: number;
    total: number;
    successful: number;
    failed: number;
    avgResponseTime: number;
    avgDuration: number;
    allFilesProcessed: number;
    someFilesUnprocessed: number;
    device: {[key in ClientDevice]: number};
    unsuccessfulSessionResultDetails: SessionResultInformationDetails;
    successfulSessionResultDetails: SessionResultInformationDetails;
}>

export type AgentActivityStatistic = Pick<
    ActivityStatistic,
    'answered' |
    'successful' |
    'failed' |
    'avgDuration' |
    'allFilesProcessed' |
    'someFilesUnprocessed' |
    'unsuccessfulSessionResultDetails' |
    'successfulSessionResultDetails'
> & {
    rating: {
        average: number;
        count: number;
    }
}

export type ActivityStatisticHours = {
    uniqueClients: number[],
    total: number[],
    answered: number[],
    failed: number[],
    missed: number[],
    successful: number[],
    allFilesProcessed?: number[],
    someFilesUnprocessed?: number[]
}

export type SessionHistoryStatistic = {
    hours: ActivityStatisticHours;
} & ActivityStatistic

export interface SessionHistoryStatisticDaily {
    [date: string]: ActivityStatistic
}
